import { useMemo } from 'react';

import {
  filterSliceItemsByLocationSlug,
  showSliceByLocationSlug,
} from '@/utils/locationSlug';
import type { SliceItemBase } from '@/utils/sliceSsr';

import { useSelectedStore } from './useSelectedStore';

type UseSliceItemsForSelectedStoreOptions<TItem extends SliceItemBase> = {
  alternateItems?: TItem[];
  initialStoreUrlSlug?: string;
  items: TItem[];
  primaryLocationSlug?: string | null;
};

/**
 * Returns slice items for the selected store. SSR `items` are used as-is when
 * the store matches the server; re-filters only on store change.
 */
const useSliceItemsForSelectedStore = <TItem extends SliceItemBase>({
  alternateItems = [],
  initialStoreUrlSlug,
  items,
  primaryLocationSlug,
}: UseSliceItemsForSelectedStoreOptions<TItem>): TItem[] => {
  const { store } = useSelectedStore();
  const selectedSlug = store?.url_slug;

  return useMemo(() => {
    if (!selectedSlug || selectedSlug === initialStoreUrlSlug) {
      return items;
    }

    if (!showSliceByLocationSlug(primaryLocationSlug, selectedSlug)) {
      return [];
    }

    return filterSliceItemsByLocationSlug(
      [...items, ...alternateItems],
      selectedSlug,
    );
  }, [
    alternateItems,
    initialStoreUrlSlug,
    items,
    primaryLocationSlug,
    selectedSlug,
  ]);
};

export { useSliceItemsForSelectedStore };
